import { registerView, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { isMobile } from "react-device-detect";
import { turnstileSiteKey } from "../../../config";
import TurnstileViewModel from "viewModels/common/turnstileViewModel";
import { getTurnStileErrorType, getMockTurnstileErrorTypes } from "entities/enumTurnstileCodes";

interface TurnstileViewProps {
  context?: string;
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
  onLoad?: () => void;
  onBeforeInteractive?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const TurnstileView: ViewComponent<TurnstileViewModel> = observer(({ vm, ...props }) => {
  const {
    context = "default",
    onSuccess,
    onError,
    onLoad,
    onBeforeInteractive,
    style,
    className = "d-flex flex-column align-items-center justify-content-center",
  } = props as TurnstileViewProps;

  const [turnstileSize] = React.useState<"compact" | "normal">(() => {
    return window.innerWidth < 420 ? "compact" : "normal";
  });
  const turnstileRef = React.useRef<TurnstileInstance>(null);
  const [turnstileKey] = React.useState(() => Math.random().toString(36));

  React.useEffect(() => {
    vm.initialize();
  }, [context, vm, vm.showInteractive, vm.shareToken]);

  React.useEffect(() => {
    if (turnstileRef.current) {
      vm.setInstance(turnstileRef.current);
    }
  }, [vm.turnstileToken]);

  const processTurnstileInfo = React.useCallback(
    (rawTurnstileInfo?: string) => {
      if (rawTurnstileInfo?.includes("%LINK%")) {
        const parts = rawTurnstileInfo.split("%LINK%");
        return (
          <>
            {parts[0]}
            <a
              target="_blank"
              rel="noopener noreferrer"
              id="data-test-turnstile-info-link"
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={e => {
                e.preventDefault();
                vm.isDisabledByTurnsitel = true;
                vm.resetError();
                turnstileRef.current?.reset();
                turnstileRef.current?.execute();
                vm.onTurnstileLoad();
              }}>
              {vm.translateTurnstile("reloadTurnstile")}
            </a>
            {parts[1]}
          </>
        );
      }
      return rawTurnstileInfo;
    },
    [vm]
  );

  const handleSuccess = React.useCallback(
    (token: string) => {
      vm.onTurnstileVerify(token);
      if (vm.isDisabledByTurnsitel) {
        vm.isDisabledByTurnsitel = false;
      }
      onSuccess?.(token);
    },
    [vm, onSuccess]
  );

  const handleError = React.useCallback(
    (error: string) => {
      const typeError = getTurnStileErrorType(error);
      const string = vm.translateTurnstile(typeError);
      vm.onTurnstileError(string);
      vm.isDisabledByTurnsitel = true;
      vm.resetToken();
      onError?.(string);
    },
    [vm, onError]
  );

  const handleTimeout = React.useCallback(() => {
    const typeError = getTurnStileErrorType("timeout");
    const string = vm.translateTurnstile(typeError);
    vm.isDisabledByTurnsitel = true;
    vm.resetToken();
    vm.onSetInfo(string);
  }, [vm]);

  const handleExpire = React.useCallback(() => {
    const typeError = getTurnStileErrorType("expired");
    const string = vm.translateTurnstile(typeError);
    vm.isDisabledByTurnsitel = true;
    vm.resetToken();
    vm.onSetInfo(string);
  }, [vm]);

  const handleLoad = React.useCallback(() => {
    vm.onTurnstileLoad();
    onLoad?.();
  }, [vm, onLoad]);

  const handleInteractive = React.useCallback(() => {
    const string = vm.translateTurnstile("interactive");
    vm.onBeforeInteractive(string);
    vm.isDisabledByTurnsitel = true;
    vm.resetToken();
    onBeforeInteractive?.();
  }, [vm, onBeforeInteractive]);

  return (
    <div className={className} style={{ transform: isMobile ? "scale(0.9)" : "scale(1)", ...style }}>
      {((vm.shareToken && vm.showInteractive) || (!vm.shareToken && !vm.showInteractive)) && (
        <div className="d-flex flex-column mx-4 mb-2" id="data-test-turnstile-info">
          {!vm.turnstileError ? (
            <span className="text-sm text-center" style={{ fontSize: "0.8rem" }}>
              {processTurnstileInfo(vm.turnstileInfo)}
            </span>
          ) : (
            <span className="block text-danger text-sm text-center" style={{ fontSize: "0.8rem" }}>
              {processTurnstileInfo(vm.turnstileError)}
            </span>
          )}
        </div>
      )}

      <div className="d-block mx-auto mb-lg-0 mw-100" data-testid="turnstile-widget">
        <Turnstile
          key={turnstileKey}
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          onSuccess={handleSuccess}
          onError={handleError}
          onTimeout={handleTimeout}
          onExpire={handleExpire}
          onBeforeInteractive={handleInteractive}
          onLoadScript={handleLoad}
          onWidgetLoad={handleLoad}
          options={{
            size: turnstileSize,
            theme: "light",
            language: vm.localizationService.locale,
            retry: "never",
            refreshExpired: "never",
            refreshTimeout: "never",
            execution: !!vm.shareToken ? "execute" : "render",
            appearance: !!vm.shareToken ? "execute" : "always",
          }}
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        />
      </div>
    </div>
  );
});

registerView(TurnstileView, TurnstileViewModel);
